import { useCallback, useEffect, useRef } from "react";

import { useSearchParams } from "react-router";

import {
  QueryScenariosOrderBy,
  QueryScenariosStatus,
  VisibilityScope,
} from "@/graphql";

export const ASSIGNED_SCENARIO_SEARCH_VALUE_PREFIX = "_assigned";

const SCENARIO_VIEW_ALL_SEARCH_VALUE = "_all";

export const generateAssignedScenarioSearchValue = (userId: string) => {
  return `${ASSIGNED_SCENARIO_SEARCH_VALUE_PREFIX}_${userId}`;
};

export const parseScenarioOwnerSearchValue = (
  value?: string | null,
  userId?: string | null,
) => {
  const isAssignee =
    !!userId && value === generateAssignedScenarioSearchValue(userId);
  const isOwner = value === userId;

  return {
    isOwner,
    isAssignee,
  };
};

export const useScenarioQueryParams = ({
  statusFilter,
  userId,
}: {
  statusFilter?: QueryScenariosStatus;
  userId?: string | null;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract values from searchParams
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const selectedStatus =
    (searchParams.get("status") as QueryScenariosStatus) ||
    statusFilter ||
    QueryScenariosStatus.All;

  // Parse selectedScopes from searchParams
  const scopesParam = searchParams.get("scopes");
  const selectedScopes = scopesParam
    ? (scopesParam.split(",") as VisibilityScope[])
    : [];

  // Parse selectedCollections from searchParams
  const collectionsParam = searchParams.get("collections");
  const collectionParamsRef = useRef<{
    isViewAll: boolean;
    collections: string[];
  }>({
    isViewAll: false,
    collections: [],
  });
  const selectedCollections = collectionParamsRef.current.collections;
  const isViewAllCollections = collectionParamsRef.current.isViewAll;

  const selectedOwner = searchParams.get("owner") || null;
  const { isOwner, isAssignee } = parseScenarioOwnerSearchValue(
    selectedOwner,
    userId,
  );

  // Parse order from searchParams
  const orderParam = searchParams.get("order");
  const selectedOrder =
    (orderParam as QueryScenariosOrderBy) || QueryScenariosOrderBy.Default;

  // Parse selectedTags from searchParams
  const tagsParam = searchParams.get("tags");
  const selectedTags = tagsParam ? tagsParam.split(",") : [];

  const query = searchParams.get("query") || null;

  // Parse selectedHasPracticed from searchParams
  const hasPracticedParam = searchParams.get("hasPracticed");
  const selectedHasPracticed =
    hasPracticedParam === "true"
      ? true
      : hasPracticedParam === "false"
        ? false
        : null;

  // Setter functions that update searchParams
  const setCurrentPage = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      if (page === 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedStatus = useCallback(
    (status: QueryScenariosStatus) => {
      const newParams = new URLSearchParams(searchParams);
      if (status !== QueryScenariosStatus.All) {
        newParams.set("status", status);
      } else {
        newParams.delete("status");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedScopes = useCallback(
    (scopes: VisibilityScope[]) => {
      const newParams = new URLSearchParams(searchParams);
      if (scopes.length > 0) {
        newParams.set("scopes", scopes.join(","));
      } else {
        newParams.delete("scopes");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedCollections = useCallback(
    (collections: string[]) => {
      collectionParamsRef.current.collections = collections;
      if (collectionParamsRef.current.isViewAll) {
        return;
      }

      const newParams = new URLSearchParams(searchParams);
      if (collections.length > 0) {
        newParams.set("collections", collections.join(","));
      } else {
        newParams.delete("collections");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, collectionParamsRef],
  );

  const setIsViewAllCollections = useCallback(
    (isViewAll: boolean) => {
      collectionParamsRef.current.isViewAll = isViewAll;
      if (!isViewAll) {
        return;
      }

      const newParams = new URLSearchParams(searchParams);
      newParams.set("collections", SCENARIO_VIEW_ALL_SEARCH_VALUE);
      setSearchParams(newParams);
    },
    [collectionParamsRef, searchParams, setSearchParams],
  );

  const setSelectedOwner = useCallback(
    (value: string | null) => {
      const newParams = new URLSearchParams(searchParams);

      if (value) {
        newParams.set("owner", value);
      } else {
        newParams.delete("owner");
      }

      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedOrder = useCallback(
    (order: QueryScenariosOrderBy) => {
      const newParams = new URLSearchParams(searchParams);
      if (order !== QueryScenariosOrderBy.Default) {
        newParams.set("order", order);
      } else {
        newParams.delete("order");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedTags = useCallback(
    (tags: string[]) => {
      const newParams = new URLSearchParams(searchParams);
      if (tags.length > 0) {
        newParams.set("tags", tags.join(","));
      } else {
        newParams.delete("tags");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setQuery = useCallback(
    (queryValue: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (queryValue) {
        newParams.set("query", queryValue);
      } else {
        newParams.delete("query");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedHasPracticed = useCallback(
    (hasPracticed: boolean | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (hasPracticed !== null) {
        newParams.set("hasPracticed", hasPracticed.toString());
      } else {
        newParams.delete("hasPracticed");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    collectionParamsRef.current.isViewAll =
      collectionsParam === SCENARIO_VIEW_ALL_SEARCH_VALUE;

    if (!collectionParamsRef.current.isViewAll) {
      collectionParamsRef.current.collections = collectionsParam
        ? collectionsParam.split(",")
        : [];
    }
  }, [collectionsParam]);

  return {
    currentPage,
    setCurrentPage,
    selectedStatus,
    setSelectedStatus,
    selectedScopes,
    setSelectedScopes,
    selectedCollections,
    setSelectedCollections,
    isViewAllCollections,
    setIsViewAllCollections,
    selectedOwner,
    isAssignee,
    isOwner,
    setSelectedOwner,
    selectedOrder,
    setSelectedOrder,
    selectedTags,
    setSelectedTags,
    query,
    setQuery,
    selectedHasPracticed,
    setSelectedHasPracticed,
  };
};
